/**
 * Port of `utils/validations.js` — the time-input sanitisers.
 *
 * These run on every keystroke in every time cell, so their exact behaviour (including
 * the odd cases) is what users experience as "the box won't let me type that". Ported
 * statement-for-statement rather than rewritten.
 *
 * Not carried over: the block of key-code constants at the top (only `ALT_KEY_CODE` was
 * exported, and nothing imported it), the fully commented-out
 * `inputValidationForRemainingHours`, and `preventPasteAction` — which was never
 * exported and reads `event.Event.ctrlKey`, a property that does not exist on a DOM
 * event, so it would throw if it were ever called.
 *
 * Also note `validation1`, which mainMenu.jsx imports from here (mainMenu.jsx:37): it is
 * not defined in this module and resolves to `undefined`. It is never called, so the
 * import is simply dropped.
 */

/** The unfreeze reason list. `key: 1` is the "~~None~~" placeholder. */
export interface UnfreezeReason {
  key: number;
  value: string;
}

/**
 * Trailing spaces in "Tasks not assigned " and "Login issues " are in the original and
 * are sent to ServiceNow verbatim as `reasonToUnfreeze`, so they are preserved.
 */
export const reasonsToUnfreeze: UnfreezeReason[] = [
  { key: 1, value: '~~None~~' },
  { key: 2, value: 'On Unplanned long leave/Emergency leave/ Medical leave' },
  { key: 3, value: 'Tasks not assigned ' },
  { key: 4, value: 'No Allocation in the Project assigned to' },
  { key: 5, value: 'Login issues ' },
  { key: 6, value: 'Missed to enter time' },
  { key: 7, value: 'Any other reason, please describe' },
];

/** Keys rejected outright by the time inputs — "e"/"E" would make "1e5" a valid number. */
export const blockInvalidChar = ['e', 'E'];

/**
 * keyUp handler for a time cell.
 *
 * Anything that is not a number is discarded, except a lone "." which becomes "0.".
 * Note the original's branches for "", null, ".." and "..." all produce the same "" as
 * the final else — they are kept as written rather than collapsed, since the structure
 * is what documents which inputs were being considered.
 */
export function inputValidation(actualValue: string, maxLength: number): string {
  if (isNaN(Number(actualValue.toString()))) {
    if (actualValue === '.' && actualValue.length === 1) {
      return '0.';
    }
    if (
      actualValue === '' ||
      actualValue === null ||
      actualValue === '..' ||
      actualValue === '...'
    ) {
      return '';
    }
    return '';
  }
  return numberValidation(actualValue, maxLength);
}

/** Truncation rules for a numeric time value. */
export function numberValidation(actualValue: string, maxLength: number): string {
  let value = actualValue;

  // '123' -> '12' when there is no decimal point.
  if (value.toString().length > maxLength && value.toString().indexOf('.') < 0) {
    value = value.slice(0, maxLength);
  }

  // Leading zero: '09' -> '9'.
  if (value.toString().length === maxLength && value.toString().indexOf('0') === 0) {
    if (value.toString().indexOf('.') < 0) {
      value = value.slice(1, 2);
    }
  }

  // '00' -> '0'.
  if (value === '00') {
    value = '0';
  }

  // '.999' -> '.99'
  if (value.toString().indexOf('.') === 0) {
    const splitNumber = value.toString().split('.');
    if ((splitNumber[1] as string).length > 2) {
      const newDecimalValue = (splitNumber[1] as string).slice(0, 2);
      value = `${splitNumber[0]}.${newDecimalValue}`;
    }
  }

  // '23.223' -> '23.22', and cap the integer part at maxLength.
  if (value.toString().indexOf('.') > 0) {
    const splitNumber = value.toString().split('.');
    if ((splitNumber[0] as string).length > maxLength) {
      splitNumber[0] = (splitNumber[0] as string).slice(0, maxLength);
      if ((splitNumber[1] as string).length > 0) {
        const newDecimalValue = (splitNumber[1] as string).slice(0, 2);
        value = `${splitNumber[0]}.${newDecimalValue}`;
      }
    }

    if ((splitNumber[1] as string).length > 2) {
      const newDecimalValue = (splitNumber[1] as string).slice(0, 2);
      value = `${splitNumber[0]}.${newDecimalValue}`;
    }
  }

  /*
   * "if the end decimal point is 0, rounded up (9.10 -> 9.1)".
   *
   * The original's last line is `splitNumber[1].toString().split(0, 1)` — String.split
   * with a NUMBER separator, which coerces to the string "0" and splits on it, with a
   * limit of 1. For "10" that yields ["1"], which stringifies back to "1", so the stated
   * intent happens to be met. For "20" it yields ["2"], and so on. Reproduced exactly,
   * including the array-to-string coercion, because any "cleaner" rewrite would differ
   * on inputs like "9.00".
   */
  if (
    value.toString().indexOf('.') > 0 &&
    (value.length === maxLength + 2 || value.length === maxLength + 3)
  ) {
    const splitNumber = value.toString().split('.');
    const intArr = Array.from(String(splitNumber[1]), (num) => Number(num));
    if (intArr.length > 1 && intArr[intArr.length - 1] === 0) {
      value = `${splitNumber[0]}.${(splitNumber[1] as string).split('0', 1)}`;
    }
  }

  return value;
}

/**
 * blur handler — drops a trailing "." or a single trailing zero ("9." -> 9, "9.0" -> 9).
 *
 * Returns a NUMBER when the value contained a decimal point and a STRING otherwise; the
 * original did the same, and call sites write the result straight into state, so the
 * mixed return type is part of the contract.
 */
export function inputValidationOnBlur(actualValue: string | number): string | number {
  if (actualValue.toString().indexOf('.') > 0) {
    const splitNumber = actualValue.toString().split('.');
    let value: string = actualValue.toString();

    if ((splitNumber[1] as string).length === 0) {
      value = splitNumber[0] as string;
    } else if ((splitNumber[1] as string).length === 1 && Number(splitNumber[1]) === 0) {
      value = splitNumber[0] as string;
    }
    return Number(value);
  }

  return actualValue;
}
